import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_PACK_SIZES = { 1: 300, 2: 150, 3: 100, 5: 100, 10: 60, 20: 30, 30: 30, 50: 30 };
const DENOMINATIONS = [1, 2, 3, 5, 10, 20, 30, 50];

export const useAdminData = (enabled, onUnauthorized) => {
  const [users, setUsers] = useState([]);
  const [accessCodes, setAccessCodes] = useState([]);
  const [recoveryPhrase, setRecoveryPhrase] = useState('');
  const [packSizes, setPackSizes] = useState(DEFAULT_PACK_SIZES);
  const [supportContact, setSupportContact] = useState({ name: 'Bob', phone: '859-537-5780' });
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/users`);
      setUsers(res.data.users);
    } catch (err) {
      if (err.response?.status === 403) onUnauthorized?.();
      else toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [onUnauthorized]);

  const fetchAccessCodes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/access-codes`);
      setAccessCodes(res.data.codes);
    } catch (err) {
      console.error("Failed to fetch access codes:", err);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/notifications`);
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Failed to fetch notifications:", err);
      }
    }
  }, []);

  const markNotificationsRead = useCallback(async () => {
    try {
      await axios.put(`${API}/api/admin/notifications/read`);
      setUnreadCount(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Failed to mark read:", err);
      }
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    const loadAll = async () => {
      try {
        const [usersRes, codesRes, phraseRes, settingsRes, notesRes] = await Promise.all([
          axios.get(`${API}/api/admin/users`),
          axios.get(`${API}/api/admin/access-codes`),
          axios.get(`${API}/api/admin/recovery-phrase`),
          axios.get(`${API}/api/admin/settings`),
          axios.get(`${API}/api/admin/notifications`),
        ]);
        if (cancelled) return;
        setUsers(usersRes.data.users);
        setAccessCodes(codesRes.data.codes);
        setRecoveryPhrase(phraseRes.data.is_set ? true : '');
        if (settingsRes.data.pack_sizes) {
          const ps = {};
          DENOMINATIONS.forEach((d) => {
            ps[d] = parseInt(settingsRes.data.pack_sizes[d]) || DEFAULT_PACK_SIZES[d];
          });
          setPackSizes(ps);
        }
        if (settingsRes.data.support_contact) {
          setSupportContact({
            name: settingsRes.data.support_contact.name || 'Bob',
            phone: settingsRes.data.support_contact.phone || '859-537-5780',
          });
        }
        setNotifications(notesRes.data.notifications || []);
        setUnreadCount(notesRes.data.unread_count || 0);
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 403) onUnauthorized?.();
        else console.error("Failed to load admin data:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadAll();
    const interval = setInterval(() => {
      fetchUsers();
      fetchAccessCodes();
      fetchNotifications();
    }, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [enabled, onUnauthorized, fetchUsers, fetchAccessCodes, fetchNotifications]);

  return {
    users,
    accessCodes,
    recoveryPhrase,
    packSizes,
    supportContact,
    notifications,
    unreadCount,
    loading,
    setPackSizes,
    setRecoveryPhrase,
    setSupportContact,
    fetchUsers,
    fetchAccessCodes,
    fetchNotifications,
    markNotificationsRead,
  };
};
